import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Eye,
  EyeOff,
  ArrowRight,
  ArrowLeft,
  Building2,
  Check,
  Loader2,
  AlertTriangle,
  ShieldCheck,
} from 'lucide-react'
import { useMutation } from '@apollo/client/react'
import { Logo } from '@/features/marketing/components/Logo'
import { tokenStore } from '@/lib/auth/tokenStore'
import { routes } from '@/shared/constants'
import { useBusinessCategories } from '@/features/businesses/hooks/useBusinessCategories'
import { usePackageCatalog } from '@/features/services/hooks/usePackageCatalog'
import { formatServicePrice } from '@/features/services/utils/formatServicePrice'
import { useCountries } from '@/shared/hooks/useCountries'
import { usePaymentGateways } from '@/features/payments/hooks/usePaymentGateways'
import { GatewayPicker } from '@/features/payments/components/GatewayPicker'
import {
  PaymentLinkPanel,
  type PaymentLinkData,
} from '@/features/payments/components/PaymentLinkPanel'
import {
  PortalCreateBusinessAndPaymentLinkDocument,
  PortalCreateBillingPaymentLinkDocument,
} from '@/graphql/generated/graphql'
import { PORTAL_REGISTER, PORTAL_VERIFY_CODE, PORTAL_RESEND_CODE } from '../api/mutations'
import { parseRegisterError, parseVerifyError } from '../helpers/authErrors'
import { graphQLErrorMessage, isConnectivityError } from '@/lib/apollo/errors'

type Billing = 'MONTHLY' | 'ANNUAL'
const TOTAL_STEPS = 4

/** Resultado del alta+link: la pantalla de pago vive sobre este estado. */
interface PaymentState {
  billingId: number
  totalReal: number
  currencyName: string
  link: PaymentLinkData | null
  linkError: string | null
}

interface PortalRegisterData { portalRegister: boolean }
interface PortalVerifyCodeData { portalVerifyCode: { accessToken: string } }

export function RegisterPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [submitting, setSubmitting] = useState(false)
  const [show, setShow] = useState(false)

  // Paso 1 — negocio
  const [bizName, setBizName] = useState('')
  const [categoryIds, setCategoryIds] = useState<string[]>([])
  const [street, setStreet] = useState('')
  const [city, setCity] = useState('')
  const [countryId, setCountryId] = useState(0)
  const [provinceId, setProvinceId] = useState(0)
  const [municipalityId, setMunicipalityId] = useState(0)

  // Paso 2 — servicios
  const [serviceIds, setServiceIds] = useState<number[]>([])
  const [billing, setBilling] = useState<Billing>('MONTHLY')

  // Paso 3 — propietario
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [accept, setAccept] = useState(false)

  // Paso 3 sub-paso — verificación de código
  const [codeSent, setCodeSent] = useState(false)
  const [verifyCode, setVerifyCode] = useState('')
  const [registerError, setRegisterError] = useState<string | null>(null)
  const [verifyError, setVerifyError] = useState<string | null>(null)

  // Paso 4 — pago: pasarela + moneda elegidas y resultado del alta+link.
  // El negocio NO existe hasta confirmar aquí (anti-abuso, pagos-zeus-3).
  const [gatewayId, setGatewayId] = useState<number | null>(null)
  const [currencyId, setCurrencyId] = useState<number | null>(null)
  const [payment, setPayment] = useState<PaymentState | null>(null)
  const [retrying, setRetrying] = useState(false)

  // Alerta de salida con progreso
  const [leavingTo, setLeavingTo] = useState<string | null>(null)

  const { categories } = useBusinessCategories()
  const { services } = usePackageCatalog()
  const { countries } = useCountries()
  const { gateways, loading: gatewaysLoading } = usePaymentGateways()

  const [doRegister] = useMutation<PortalRegisterData>(PORTAL_REGISTER, { context: { skipAuth: true } })
  const [doVerify] = useMutation<PortalVerifyCodeData>(PORTAL_VERIFY_CODE, { context: { skipAuth: true } })
  const [doResend] = useMutation(PORTAL_RESEND_CODE, { context: { skipAuth: true } })
  const [doCreateAndLink] = useMutation(PortalCreateBusinessAndPaymentLinkDocument)
  const [doRetryLink] = useMutation(PortalCreateBillingPaymentLinkDocument)

  // Autoselección de país si solo hay uno
  useEffect(() => {
    if (countries.length === 1 && !countryId) setCountryId(Number(countries[0].id))
  }, [countries, countryId])

  const country = countries.find((c) => Number(c.id) === countryId)
  const provinces = country?.provinces ?? []
  const province = provinces.find((p) => Number(p.id) === provinceId)
  const municipalities = province?.municipalities ?? []

  const annual = billing === 'ANNUAL'
  const selectedServices = services.filter((s) => serviceIds.includes(s.id))
  const subtotal = selectedServices.reduce((sum, s) => sum + (annual ? s.annualPrice : s.monthlyPrice), 0)
  const currencyName = selectedServices[0]?.currencyName ?? ''

  const passwordsMatch = password.length > 0 && password === confirmPassword

  const step1Valid =
    bizName.trim().length >= 2 && countryId > 0 && provinceId > 0 && municipalityId > 0 && categoryIds.length > 0
  const step2Valid = serviceIds.length > 0
  const step3Valid =
    firstName.trim().length >= 2 &&
    lastName.trim().length >= 2 &&
    /\S+@\S+\.\S+/.test(email) &&
    phone.trim().length >= 6 &&
    password.length >= 8 &&
    passwordsMatch &&
    accept
  const step4Valid = gatewayId !== null && currencyId !== null

  // Con el negocio ya creado (pantalla de pago) no hay progreso que perder:
  // la factura queda pendiente y se paga después desde /app/billing.
  const hasProgress =
    !payment &&
    (step > 1 ||
    bizName.trim() !== '' ||
    categoryIds.length > 0 ||
    serviceIds.length > 0 ||
    firstName.trim() !== '' ||
    lastName.trim() !== '' ||
    email.trim() !== '' ||
    phone.trim() !== '' ||
    password !== '')

  useEffect(() => {
    if (!hasProgress) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [hasProgress])

  const guardedNavigate = (to: string) => {
    if (hasProgress) setLeavingTo(to)
    else navigate(to)
  }

  const toggleCategory = (id: string) =>
    setCategoryIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  const toggleService = (id: number) =>
    setServiceIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  const handleStep3Continue = async () => {
    if (!step3Valid || submitting) return
    setRegisterError(null)
    setSubmitting(true)
    try {
      await doRegister({ variables: { email, phone, password, firstName, lastName } })
      setCodeSent(true)
      setVerifyCode('')
    } catch (err) {
      setRegisterError(parseRegisterError(err))
    } finally {
      setSubmitting(false)
    }
  }

  const handleVerifyCode = async () => {
    if (verifyCode.trim().length < 4 || submitting) return
    setVerifyError(null)
    setSubmitting(true)
    try {
      const { data } = await doVerify({ variables: { email, code: verifyCode.trim() } })
      const token = data?.portalVerifyCode?.accessToken
      if (!token) throw new Error('Respuesta inesperada del servidor')
      tokenStore.set(token)
      setStep(4)
    } catch (err) {
      setVerifyError(parseVerifyError(err))
    } finally {
      setSubmitting(false)
    }
  }

  const handleResendCode = async () => {
    try {
      await doResend({ variables: { email } })
    } catch {
      // El usuario puede reintentar manualmente si falla el reenvío.
    }
  }

  // Confirmar el pago: crea el negocio (con su factura inicial) y genera el
  // link EN LA MISMA operación. Si el link falla, el backend devuelve el
  // negocio+factura con linkError y se reintenta sin recrear nada.
  const handleSubmit = async () => {
    if (!step4Valid || submitting || gatewayId === null || currencyId === null) return
    setSubmitting(true)
    setRegisterError(null)
    try {
      const { data } = await doCreateAndLink({
        variables: {
          input: {
            businessLegalName: bizName,
            businessCategoryIds: categoryIds.map(Number),
            licenseType: billing,
            addresses: [{ street1: street, city, countryId, provinceId, municipalityId, isMain: true }],
            packageServiceIds: serviceIds,
            paymentGatewayId: gatewayId,
            currencyId,
          },
        },
      })
      const result = data?.portalCreateBusinessAndPaymentLink
      if (!result) throw new Error('Respuesta inesperada del servidor')
      if (!result.billingId) {
        // Sin factura (total 0): no hay nada que pagar, directo al panel.
        navigate(routes.app.dashboard, { replace: true })
        return
      }
      setPayment({
        billingId: result.billingId,
        totalReal: result.totalReal,
        currencyName: result.currencyName,
        link: result.link ?? null,
        linkError: result.linkError ?? null,
      })
    } catch (err) {
      setRegisterError(
        isConnectivityError(err)
          ? 'No pudimos conectar con el servidor. Verifica tu conexión e intenta de nuevo.'
          : graphQLErrorMessage(err) || 'No pudimos crear tu negocio. Intenta de nuevo.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  // Reintento/regeneración del link sobre la factura ya creada.
  const handleRetryLink = async () => {
    if (!payment || retrying || gatewayId === null || currencyId === null) return
    setRetrying(true)
    try {
      const { data } = await doRetryLink({
        variables: {
          input: {
            billingId: payment.billingId,
            paymentGatewayId: gatewayId,
            currencyId,
          },
        },
      })
      const result = data?.portalCreateBillingPaymentLink
      if (result) {
        setPayment({
          billingId: result.billingId,
          totalReal: result.totalReal,
          currencyName: result.currencyName,
          link: result.link,
          linkError: null,
        })
      }
    } catch (err) {
      setPayment((prev) =>
        prev
          ? { ...prev, linkError: graphQLErrorMessage(err) || 'No se pudo generar el link de pago. Intenta de nuevo.' }
          : prev,
      )
    } finally {
      setRetrying(false)
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50 relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(50%_40%_at_80%_0%,rgba(217,96,46,0.15),transparent_70%)]" />

      <header className="px-6 lg:px-12 py-6 flex items-center justify-between">
        <button onClick={() => guardedNavigate('/')} className="inline-flex items-center" aria-label="Inicio">
          <Logo />
        </button>
        <button onClick={() => guardedNavigate('/login')} className="text-sm font-semibold hover:text-primary">
          Iniciar sesión
        </button>
      </header>

      <main className="flex items-start justify-center px-5 py-6 lg:py-10">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-7">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-bold uppercase tracking-wider">
              <Building2 className="size-3" /> Registra tu negocio
            </span>
            <h1 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4">Crea tu cuenta TecoPOS.</h1>
            <Stepper step={step} />
          </div>

          <div className="bg-white/80 backdrop-blur-xl border border-black/5 p-6 lg:p-8 rounded-3xl shadow-sm">
            {/* ── Paso 1: Negocio ── */}
            {step === 1 && (
              <div className="space-y-5">
                <Field label="Nombre del negocio" value={bizName} onChange={setBizName} placeholder="Meraki Café" required />

                <div>
                  <span className="text-xs font-semibold text-muted-foreground">Tipo de negocio</span>
                  <p className="text-[11px] text-muted-foreground/80 mb-2">Elige una o varias categorías.</p>
                  <div className="flex flex-wrap gap-2">
                    {categories.length === 0 ? (
                      <span className="text-xs text-muted-foreground">Cargando categorías…</span>
                    ) : (
                      categories.map((c) => {
                        const on = categoryIds.includes(String(c.id))
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => toggleCategory(String(c.id))}
                            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                              on ? 'bg-foreground text-background' : 'bg-neutral-100 text-foreground hover:bg-neutral-200'
                            }`}
                          >
                            {c.name}
                          </button>
                        )
                      })
                    )}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Calle y número" value={street} onChange={setStreet} placeholder="Calle 23 #456" />
                  <Field label="Ciudad / localidad" value={city} onChange={setCity} placeholder="Vedado" />
                  <SelectField
                    label="País"
                    value={countryId}
                    onChange={(v) => { setCountryId(v); setProvinceId(0); setMunicipalityId(0) }}
                    options={countries.map((c) => ({ value: Number(c.id), label: c.name }))}
                  />
                  <SelectField
                    label="Provincia"
                    value={provinceId}
                    disabled={!countryId}
                    onChange={(v) => { setProvinceId(v); setMunicipalityId(0) }}
                    options={provinces.map((p) => ({ value: Number(p.id), label: p.name }))}
                  />
                  <SelectField
                    label="Municipio"
                    value={municipalityId}
                    disabled={!provinceId}
                    onChange={setMunicipalityId}
                    options={municipalities.map((m) => ({ value: Number(m.id), label: m.name }))}
                  />
                </div>
              </div>
            )}

            {/* ── Paso 2: Servicios ── */}
            {step === 2 && (
              <div className="space-y-5">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold">Selecciona tus servicios</span>
                  <BillingToggle value={billing} onChange={setBilling} />
                </div>

                <div className="space-y-2">
                  {services.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Cargando servicios…</p>
                  ) : (
                    services.map((s) => {
                      const on = serviceIds.includes(s.id)
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => toggleService(s.id)}
                          className={`flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition-all ${
                            on ? 'border-primary bg-primary/5' : 'border-black/10 hover:border-black/20'
                          }`}
                        >
                          <span className={`grid size-5 shrink-0 place-items-center rounded-md border ${on ? 'border-primary bg-primary text-white' : 'border-black/20'}`}>
                            {on && <Check className="size-3.5" />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold">{s.name}</span>
                            {s.description && <span className="block text-xs text-muted-foreground line-clamp-1">{s.description}</span>}
                          </span>
                          <span className="shrink-0 text-sm font-bold text-foreground">
                            {s.hasPrice ? formatServicePrice(annual ? s.annualPrice : s.monthlyPrice, s.currencyName) : 'A confirmar'}
                          </span>
                        </button>
                      )
                    })
                  )}
                </div>

                {selectedServices.length > 0 && (
                  <div className="flex items-center justify-between rounded-2xl bg-neutral-100 px-4 py-3">
                    <span className="text-sm font-semibold">Subtotal {annual ? 'anual' : 'mensual'}</span>
                    <span className="text-base font-bold text-primary">{formatServicePrice(subtotal, currencyName)}</span>
                  </div>
                )}
              </div>
            )}

            {/* ── Paso 3a: Datos del propietario ── */}
            {step === 3 && !codeSent && (
              <div className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Nombre" value={firstName} onChange={setFirstName} placeholder="Ana" required />
                  <Field label="Apellidos" value={lastName} onChange={setLastName} placeholder="Pérez García" required />
                  <Field label="Correo electrónico" type="email" value={email} onChange={setEmail} placeholder="tu@negocio.com" required />
                  <Field label="Teléfono" type="tel" value={phone} onChange={setPhone} placeholder="+53 5XXXXXXX" required />
                  <PasswordField
                    label="Contraseña"
                    value={password}
                    onChange={setPassword}
                    show={show}
                    onToggleShow={() => setShow((s) => !s)}
                    placeholder="Mínimo 8 caracteres"
                  />
                  <PasswordField
                    label="Confirmar contraseña"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    show={show}
                    placeholder="Repite la contraseña"
                    invalid={confirmPassword.length > 0 && !passwordsMatch}
                  />
                </div>

                {password.length > 0 && password.length < 8 && (
                  <p className="text-xs text-muted-foreground">La contraseña debe tener al menos 8 caracteres.</p>
                )}
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="text-xs font-semibold text-red-600">Las contraseñas no coinciden.</p>
                )}
                {confirmPassword.length > 0 && passwordsMatch && (
                  <p className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                    <Check className="size-3.5" /> Las contraseñas coinciden.
                  </p>
                )}

                <label className="flex items-start gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={accept}
                    onChange={(e) => setAccept(e.target.checked)}
                    className="mt-0.5 accent-[color:var(--primary)]"
                  />
                  <span>
                    Acepto los{' '}
                    <a href="/terminos" target="_blank" rel="noreferrer" className="font-semibold text-primary">Términos</a>
                    {' '}y la{' '}
                    <a href="/policy" target="_blank" rel="noreferrer" className="font-semibold text-primary">Política de privacidad</a>
                    {' '}de TecoPOS.
                  </span>
                </label>

                {registerError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3.5 py-2.5">
                    {registerError}
                  </p>
                )}
              </div>
            )}

            {/* ── Paso 3b: Verificación de código ── */}
            {step === 3 && codeSent && (
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <span className="grid size-11 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                    <ShieldCheck className="size-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold">Verifica tu correo</p>
                    <p className="text-xs text-muted-foreground">
                      Enviamos un código de 6 dígitos a <strong>{email}</strong>
                    </p>
                  </div>
                </div>

                <label className="block">
                  <span className="text-xs font-semibold text-muted-foreground">Código de verificación</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456"
                    className="mt-1.5 w-full px-3.5 py-4 rounded-xl border border-black/10 bg-white outline-none text-xl font-bold tracking-[0.4em] text-center focus:border-primary transition-colors"
                    disabled={submitting}
                  />
                </label>

                {verifyError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3.5 py-2.5">
                    {verifyError}
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleVerifyCode}
                  disabled={verifyCode.trim().length < 4 || submitting}
                  className="w-full py-3.5 bg-foreground text-background font-semibold rounded-xl hover:bg-primary transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting
                    ? <Loader2 className="size-4 animate-spin" />
                    : <><ShieldCheck className="size-4" /> Verificar código</>
                  }
                </button>

                <p className="text-xs text-center text-muted-foreground">
                  ¿No recibiste el código?{' '}
                  <button type="button" onClick={handleResendCode} className="font-semibold text-primary hover:underline">
                    Reenviar
                  </button>
                </p>
              </div>
            )}

            {/* ── Paso 4: Pago ── */}
            {step === 4 && !payment && (
              <div className="space-y-5">
                <div>
                  <span className="text-sm font-semibold">Elige cómo pagar</span>
                  <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                    Tu negocio y su factura se crean al confirmar; el cobro se
                    procesa de forma segura con la pasarela elegida.
                  </p>
                </div>

                <GatewayPicker
                  gateways={gateways}
                  loading={gatewaysLoading}
                  selectedGatewayId={gatewayId}
                  selectedCurrencyId={currencyId}
                  fallbackCurrency={
                    selectedServices[0]?.currencyId
                      ? { id: selectedServices[0].currencyId, name: selectedServices[0].currencyName }
                      : null
                  }
                  onSelect={(gId, cId) => {
                    setGatewayId(gId)
                    setCurrencyId(cId)
                  }}
                  onSelectCurrency={setCurrencyId}
                />

                {selectedServices.length > 0 && (
                  <div className="flex items-center justify-between rounded-2xl bg-neutral-100 px-4 py-3">
                    <span className="text-sm font-semibold">Subtotal {annual ? 'anual' : 'mensual'} estimado</span>
                    <span className="text-base font-bold text-primary">{formatServicePrice(subtotal, currencyName)}</span>
                  </div>
                )}

                {registerError && (
                  <p className="text-sm text-red-600" role="alert">{registerError}</p>
                )}
              </div>
            )}

            {/* ── Paso 4: pantalla de pago (negocio+factura ya creados) ── */}
            {step === 4 && payment && (
              <div className="space-y-5">
                <div>
                  <span className="text-sm font-semibold">Completa tu pago</span>
                  <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                    Tu negocio quedó creado con su factura. Al confirmarse el
                    pago se activa automáticamente.
                  </p>
                </div>
                <PaymentLinkPanel
                  billingId={payment.billingId}
                  totalReal={payment.totalReal}
                  currencyName={payment.currencyName}
                  link={payment.link}
                  linkError={payment.linkError}
                  retrying={retrying}
                  onRetry={() => void handleRetryLink()}
                  onPaid={() => navigate(routes.app.dashboard, { replace: true })}
                />
                <p className="text-center text-xs text-muted-foreground">
                  ¿Prefieres pagar más tarde?{' '}
                  <button
                    type="button"
                    onClick={() => navigate(routes.app.dashboard, { replace: true })}
                    className="font-semibold text-primary"
                  >
                    Ir a mi panel
                  </button>{' '}
                  — tu factura queda pendiente en Facturación y el negocio se
                  activará cuando la pagues.
                </p>
              </div>
            )}

            {/* ── Navegación ── */}
            {!codeSent && !payment && (
              <div className="mt-7 flex items-center gap-3">
                {step > 1 && (
                  <button
                    onClick={() => setStep((s) => s - 1)}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="size-4" /> Atrás
                  </button>
                )}
                {step < TOTAL_STEPS ? (
                  <button
                    onClick={step === 3 ? handleStep3Continue : () => setStep((s) => s + 1)}
                    disabled={
                      submitting ||
                      (step === 1 && !step1Valid) ||
                      (step === 2 && !step2Valid) ||
                      (step === 3 && !step3Valid)
                    }
                    className="ml-auto inline-flex items-center justify-center gap-2 rounded-xl bg-foreground px-6 py-3 text-sm font-semibold text-background transition-colors hover:bg-primary disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {submitting && step === 3
                      ? <Loader2 className="size-4 animate-spin" />
                      : <>Continuar <ArrowRight className="size-4" /></>
                    }
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={!step4Valid || submitting}
                    className="ml-auto inline-flex items-center justify-center gap-2 rounded-xl bg-foreground px-6 py-3 text-sm font-semibold text-background transition-colors hover:bg-primary disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {submitting ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
                    Crear negocio y pagar
                  </button>
                )}
              </div>
            )}

            {codeSent && (
              <div className="mt-7">
                <button
                  onClick={() => { setCodeSent(false); setVerifyError(null) }}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="size-4" /> Cambiar datos
                </button>
              </div>
            )}
          </div>

          <p className="text-sm text-center text-muted-foreground mt-6">
            ¿Ya tienes cuenta?{' '}
            <button onClick={() => guardedNavigate('/login')} className="font-semibold text-primary">
              Iniciar sesión
            </button>
          </p>
        </div>
      </main>

      {/* Alerta: salir con progreso */}
      {leavingTo && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-5"
          onClick={() => setLeavingTo(null)}
        >
          <div
            className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <span className="grid size-10 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-600">
                <AlertTriangle className="size-5" />
              </span>
              <div>
                <h2 className="text-base font-bold">¿Salir del registro?</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Perderás todo el progreso que llevas en este formulario.
                </p>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setLeavingTo(null)}
                className="text-sm font-semibold text-muted-foreground hover:text-foreground"
              >
                Seguir aquí
              </button>
              <button
                onClick={() => navigate(leavingTo)}
                className="rounded-xl bg-foreground px-4 py-2.5 text-sm font-semibold text-background hover:bg-primary"
              >
                Salir igualmente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Stepper({ step }: { step: number }) {
  const labels = ['Tu negocio', 'Servicios', 'Tu cuenta', 'Pago']
  return (
    <div className="mt-5 flex items-center justify-center gap-2">
      {labels.map((label, i) => {
        const n = i + 1
        const done = step > n
        const active = step === n
        return (
          <div key={label} className="flex items-center gap-2">
            <span
              className={`grid size-6 place-items-center rounded-full text-[11px] font-bold ${
                active ? 'bg-primary text-white' : done ? 'bg-foreground text-background' : 'bg-neutral-200 text-muted-foreground'
              }`}
            >
              {done ? <Check className="size-3" /> : n}
            </span>
            <span className={`text-xs font-semibold ${active ? 'text-foreground' : 'text-muted-foreground'} hidden sm:inline`}>
              {label}
            </span>
            {n < labels.length && <span className="mx-1 h-px w-4 bg-neutral-300" />}
          </div>
        )
      })}
    </div>
  )
}

function BillingToggle({ value, onChange }: { value: Billing; onChange: (v: Billing) => void }) {
  return (
    <div className="inline-flex rounded-full border border-black/10 bg-white p-0.5 text-xs font-semibold">
      {(['MONTHLY', 'ANNUAL'] as Billing[]).map((b) => (
        <button
          key={b}
          type="button"
          onClick={() => onChange(b)}
          className={`rounded-full px-3 py-1.5 transition-colors ${value === b ? 'bg-foreground text-background' : 'text-muted-foreground'}`}
        >
          {b === 'MONTHLY' ? 'Mensual' : 'Anual'}
        </button>
      ))}
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  placeholder?: string
  required?: boolean
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-muted-foreground">{label}{required && ' *'}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full px-3.5 py-3 rounded-xl border border-black/10 bg-white outline-none text-sm focus:border-primary transition-colors"
      />
    </label>
  )
}

function SelectField({
  label,
  value,
  onChange,
  options,
  disabled,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  options: { value: number; label: string }[]
  disabled?: boolean
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      <select
        value={value || ''}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-1.5 w-full px-3.5 py-3 rounded-xl border border-black/10 bg-white outline-none text-sm focus:border-primary transition-colors disabled:opacity-50"
      >
        <option value="">Selecciona…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  )
}

function PasswordField({
  label,
  value,
  onChange,
  show,
  onToggleShow,
  placeholder,
  invalid,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  show: boolean
  onToggleShow?: () => void
  placeholder?: string
  invalid?: boolean
}) {
  return (
    <div>
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      <div className={`mt-1.5 flex items-center gap-2 px-3.5 py-3 rounded-xl border bg-white focus-within:border-primary ${invalid ? 'border-red-400' : 'border-black/10'}`}>
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-transparent outline-none text-sm"
        />
        {onToggleShow && (
          <button type="button" onClick={onToggleShow} className="text-muted-foreground">
            {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
        )}
      </div>
    </div>
  )
}

